import { GLOBAL_POLL_TIMEOUT_MS, getDelayAfterPoll } from '@/lib/constants/poll';
import { getDownloadSessionGeneration } from '@/lib/download/download-job-session';
import { JobService } from '@/services/job.service';
import { DownloadProjectImagesResult } from '@/types/download';
import { JobResult } from '@/types/job';
import { AdminMetricsResult } from '@/types/metrics';
import { JobStatus, JobType } from '@/types/schema';
import { useEffect, useRef, useState } from 'react';

export interface UseJobPollingOptions {
  jobId: string | null;
  suppressDownloadSessionAbort?: boolean;
  timeoutMs?: number;
  // eslint-disable-next-line no-unused-vars
  getDelayMs?: (pollsCompleted: number) => number;
  // eslint-disable-next-line no-unused-vars
  onCompleted: (result: JobResult) => void;
  // eslint-disable-next-line no-unused-vars
  onFailed: (error?: string) => void;
  onTimeout: () => void;
  onAborted?: () => void;
}

export type PollingData = {
  cancelled: boolean;
  settled: boolean;
  baselineGen: number;
  startedAt: number;
  jobId: string;
  pollsCompleted: number;
  pendingTimeoutId: ReturnType<typeof setTimeout> | null;
  isPaused: boolean;
};

export function useJobPolling({
  jobId,
  suppressDownloadSessionAbort = false,
  timeoutMs = GLOBAL_POLL_TIMEOUT_MS,
  getDelayMs = getDelayAfterPoll,
  onCompleted,
  onFailed,
  onTimeout,
  onAborted,
}: UseJobPollingOptions) {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [currentBatch, setCurrentBatch] = useState<number | null>(null);
  const [totalImages, setTotalImages] = useState<number | null>(null);
  const [totalBatches, setTotalBatches] = useState<number | null>(null);
  const pollingDataRef = useRef<PollingData | null>(null);

  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);
  const onTimeoutRef = useRef(onTimeout);
  const onAbortedRef = useRef(onAborted);
  const getDelayMsRef = useRef(getDelayMs);
  const suppressDownloadSessionAbortRef = useRef(suppressDownloadSessionAbort);
  const kickoffRef = useRef<() => Promise<void>>(async () => {});
  const visibilityHandlerRef = useRef<() => void>(() => {});

  onCompletedRef.current = onCompleted;
  onFailedRef.current = onFailed;
  onTimeoutRef.current = onTimeout;
  onAbortedRef.current = onAborted;
  getDelayMsRef.current = getDelayMs;
  suppressDownloadSessionAbortRef.current = suppressDownloadSessionAbort;

  function isSuperseded(baselineGen: number) {
    if (suppressDownloadSessionAbortRef.current) return false;
    return baselineGen !== getDownloadSessionGeneration();
  }

  function clearPendingTimeout(pollingData: PollingData) {
    if (pollingData.pendingTimeoutId !== null) {
      clearTimeout(pollingData.pendingTimeoutId);
      pollingData.pendingTimeoutId = null;
    }
  }

  function settle(run: () => void) {
    const pollingData = pollingDataRef.current;
    if (!pollingData || pollingData.settled) return;
    pollingData.settled = true;
    pollingData.cancelled = true;
    clearPendingTimeout(pollingData);
    run();
  }

  function abortIfSuperseded(pollingData: PollingData) {
    if (pollingData.cancelled || pollingData.settled) return true;
    if (isSuperseded(pollingData.baselineGen)) {
      settle(() => onAbortedRef.current?.());
      return true;
    }
    return false;
  }

  function handleTimeout() {
    const pollingData = pollingDataRef.current;
    if (!pollingData) return;
    if (abortIfSuperseded(pollingData)) return;
    settle(() => onTimeoutRef.current());
  }

  async function pollOnce(): Promise<boolean> {
    const pollingData = pollingDataRef.current;
    if (!pollingData || pollingData.cancelled) return true;
    if (isSuperseded(pollingData.baselineGen)) {
      settle(() => onAbortedRef.current?.());
      return true;
    }
    if (Date.now() - pollingData.startedAt >= timeoutMs) {
      handleTimeout();
      return true;
    }

    try {
      const job = await JobService.getJob(pollingData.jobId);

      const currentPollingData = pollingDataRef.current;
      if (!currentPollingData || currentPollingData.cancelled) return true;
      if (isSuperseded(currentPollingData.baselineGen)) {
        settle(() => onAbortedRef.current?.());
        return true;
      }
      if (Date.now() - currentPollingData.startedAt >= timeoutMs) {
        handleTimeout();
        return true;
      }

      setStatus(job.status);
      setProgress(job.progress);
      setCurrentBatch(job.currentBatch ?? null);

      if (job.type === JobType.IMAGE_DOWNLOAD) {
        const result = job.result as DownloadProjectImagesResult | undefined;
        if (result?.totalImages != null && result.totalImages > 0) {
          setTotalImages(result.totalImages);
        }
        if (job.totalBatches != null && job.totalBatches > 0) {
          setTotalBatches(job.totalBatches);
        }
      }

      if (job.status === JobStatus.COMPLETED) {
        if (job.type === JobType.IMAGE_DOWNLOAD) {
          const result = job.result as DownloadProjectImagesResult;
          const signedUrls = result.signedUrls;
          if (!signedUrls) {
            settle(() => onFailedRef.current('Missing signed URLs'));
            return true;
          }
          settle(() => onCompletedRef.current(result));
          return true;
        } else if (job.type === JobType.METRICS_CALCULATION) {
          const result = job.result as AdminMetricsResult;
          settle(() => onCompletedRef.current(result));
          return true;
        } else if (job.type === JobType.GARMENT_COMBINATION) {
          const result = job.result as JobResult;
          settle(() => onCompletedRef.current(result));
          return true;
        }
      }
      if (job.status === JobStatus.FAILED || job.status === JobStatus.CANCELLED) {
        settle(() => onFailedRef.current(job.errorMessage));
        return true;
      }

      currentPollingData.pollsCompleted += 1;
      return false;
    } catch (e) {
      const currentPollingData = pollingDataRef.current;
      if (!currentPollingData || currentPollingData.cancelled) return true;
      if (isSuperseded(currentPollingData.baselineGen)) {
        settle(() => onAbortedRef.current?.());
        return true;
      }
      settle(() => onFailedRef.current(e instanceof Error ? e.message : undefined));
      return true;
    }
  }

  function scheduleNext() {
    const pollingData = pollingDataRef.current;
    if (!pollingData || pollingData.cancelled) return;
    if (abortIfSuperseded(pollingData)) return;
    if (Date.now() - pollingData.startedAt >= timeoutMs) {
      handleTimeout();
      return;
    }
    clearPendingTimeout(pollingData);
    pollingData.pendingTimeoutId = setTimeout(() => {
      const currentPollingData = pollingDataRef.current;
      if (currentPollingData) currentPollingData.pendingTimeoutId = null;
      void runAfterDelay();
    }, getDelayMsRef.current(pollingData.pollsCompleted));
  }

  async function runAfterDelay() {
    const pollingData = pollingDataRef.current;
    if (!pollingData) return;
    const stop = await pollOnce();
    const newPollingData = pollingDataRef.current;
    if (!newPollingData || newPollingData.cancelled || newPollingData.isPaused || stop) return;
    scheduleNext();
  }

  async function kickoff() {
    const pollingData = pollingDataRef.current;
    if (!pollingData) return;
    const stop = await pollOnce();
    const newPollingData = pollingDataRef.current;
    if (!newPollingData || newPollingData.cancelled || newPollingData.isPaused || stop) return;
    scheduleNext();
  }

  function handleVisibilityChange() {
    const pollingData = pollingDataRef.current;
    if (!pollingData || pollingData.cancelled || pollingData.settled) return;

    if (document.visibilityState === 'hidden') {
      clearPendingTimeout(pollingData);
      pollingData.isPaused = true;
      return;
    }

    if (document.visibilityState === 'visible' && pollingData.isPaused) {
      pollingData.isPaused = false;
      void (async () => {
        const stop = await pollOnce();
        const newPollingData = pollingDataRef.current;
        if (!newPollingData || newPollingData.cancelled || newPollingData.isPaused || stop) return;
        scheduleNext();
      })();
    }
  }

  kickoffRef.current = kickoff;
  visibilityHandlerRef.current = handleVisibilityChange;

  useEffect(() => {
    if (!jobId) {
      pollingDataRef.current = null;
      setStatus(null);
      setProgress(null);
      setCurrentBatch(null);
      setTotalBatches(null);
      setTotalImages(null);
      return;
    }

    setStatus(null);
    setProgress(null);
    setCurrentBatch(null);
    setTotalBatches(null);
    setTotalImages(null);

    pollingDataRef.current = {
      cancelled: false,
      settled: false,
      baselineGen: getDownloadSessionGeneration(),
      startedAt: Date.now(),
      jobId,
      pollsCompleted: 0,
      pendingTimeoutId: null,
      isPaused: false,
    };

    const onVisibility = () => visibilityHandlerRef.current();
    document.addEventListener('visibilitychange', onVisibility);
    void kickoffRef.current();

    return () => {
      const pollingData = pollingDataRef.current;
      if (pollingData) {
        pollingData.cancelled = true;
        clearPendingTimeout(pollingData);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [jobId, timeoutMs]);

  return { status, progress, currentBatch, totalImages, totalBatches };
}
